import { safeFileName, brandHeaderHtml, hasOwnLogo } from "@/lib/print";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

const dataCache = new Map();
const toDataUrl = async (src) => {
  if (!src || src.startsWith("data:")) return src;
  if (dataCache.has(src)) return dataCache.get(src);
  const p = fetch(src, { credentials: "include" }).then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.blob(); })
    .then((b) => new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(b); }));
  dataCache.set(src, p);
  return p;
};

// html2canvas (foreignObject) cannot paint blob:/cross-origin images → inline every <img> as a data URL first.
export async function inlineImages(root) {
  await Promise.all(Array.from(root.querySelectorAll("img")).map(async (im) => {
    const src = im.currentSrc || im.src;
    try { im.src = await toDataUrl(src); } catch { im.remove(); }
  }));
}

export async function elementToPdf(el, filename, { receipt = false, brand = true, heading = "" } = {}) {
  filename = safeFileName(String(filename).replace(/\.pdf$/i, "")) + ".pdf";
  // Render a detached clone at the top of the page so scrolled dialogs/containers never clip the capture.
  // Documents render at A4 width (794px @96dpi) so the layout matches paper instead of the dialog column; receipts keep their 80mm width.
  const baseW = receipt ? Math.ceil(el.getBoundingClientRect().width || el.offsetWidth || 360) : 794;
  const clone = el.cloneNode(true);
  const wrap = document.createElement("div");
  wrap.style.cssText = `position:fixed;left:0;top:0;z-index:-1;pointer-events:none;background:#fff;padding:8px;box-sizing:content-box;width:${baseW}px;direction:${document.documentElement.dir || "ltr"};font-family:${getComputedStyle(el).fontFamily};`;
  clone.style.cssText += `;margin:0;max-height:none;overflow:visible;box-shadow:none;width:${baseW}px;max-width:none;box-sizing:border-box;border:0;border-radius:0;`;
  clone.querySelectorAll(".truncate").forEach((n) => { n.classList.remove("truncate"); n.style.whiteSpace = "normal"; });
  if (document.documentElement.dir === "rtl") clone.querySelectorAll("*").forEach((n) => { n.style.letterSpacing = "0"; });
  if (brand && !hasOwnLogo(el)) wrap.insertAdjacentHTML("afterbegin", brandHeaderHtml(receipt, heading));
  wrap.appendChild(clone);
  clone.querySelectorAll(".pdf-hide").forEach((n) => { n.style.display = "none"; });
  clone.querySelectorAll(".pdf-only").forEach((n) => { n.style.display = "inline"; });
  const fix = document.createElement("style"); fix.textContent = "img,svg{display:inline-block !important} .pdf-chip{padding:2px 6px 6px !important} .pdf-page{display:flex !important;flex-direction:column !important;min-height:1052px !important} .pdf-page>.pdf-foot,.pdf-page>*>.pdf-foot:last-child{margin-top:auto !important}"; wrap.appendChild(fix);
  document.body.appendChild(wrap);
  await inlineImages(clone);
  // Super-white output: flatten near-white tinted panels to pure white and lift faint greys so print/PDF stays crisp
  const lum = (c) => { const m = c && c.match(/\d+(\.\d+)?/g); return m && m.length >= 3 ? m.slice(0, 3).map(Number) : null; };
  for (const n of [clone, ...clone.querySelectorAll("*")]) {
    const cs = getComputedStyle(n);
    const bg = lum(cs.backgroundColor);
    if (bg && bg.every((v) => v >= 245) && !(cs.backgroundColor.includes("rgba") && /,\s*0\)$/.test(cs.backgroundColor))) n.style.backgroundColor = "#ffffff";
    const fg = lum(cs.color);
    if (fg && fg.every((v) => v >= 80 && v <= 150)) n.style.color = "#333333";
    const bc = lum(cs.borderTopColor);
    if (bc && bc.every((v) => v >= 200) && cs.borderTopWidth !== "0px") n.style.borderColor = "#c8c8c8";
  }
  await Promise.all(Array.from(clone.querySelectorAll("img")).map((im) => (im.complete ? null : new Promise((r) => { im.onload = r; im.onerror = r; }))));
  if (document.fonts?.ready) await document.fonts.ready;
  await new Promise((r) => requestAnimationFrame(() => setTimeout(r, 50)));
  const W = Math.max(wrap.scrollWidth, baseW + 16), H = wrap.scrollHeight;
  // Safe break points (px from wrap top): bottoms of table rows and .pdf-block elements, so pages never cut through a row
  const wrapTop = wrap.getBoundingClientRect().top;
  const blocks = Array.from(clone.querySelectorAll(".pdf-block")).map((n) => { const r = n.getBoundingClientRect(); return [r.top - wrapTop, r.bottom - wrapTop]; });
  const inBlock = (y) => blocks.some(([t, b]) => y > t + 1 && y < b - 1);
  const breaks = [...blocks, ...Array.from(clone.querySelectorAll("tr, li, h3, h4")).map((n) => { const r = n.getBoundingClientRect(); return [r.top - wrapTop, r.bottom - wrapTop]; }).filter(([t, b]) => !inBlock(t) && !inBlock(b))];
  clone.querySelectorAll("[data-pdf-pages]").forEach((n) => { n.style.visibility = "hidden"; });
  let canvas;
  try {
    // Native canvas renderer: uses the page's real web fonts (Arabic shaping, same metrics as on screen) unlike foreignObject
    canvas = await html2canvas(wrap, { scale: 2.5, backgroundColor: "#ffffff", useCORS: true, foreignObjectRendering: false, logging: false, x: 0, y: 0, width: W, height: H, scrollX: 0, scrollY: 0, windowWidth: Math.max(document.documentElement.clientWidth, W), windowHeight: Math.max(document.documentElement.clientHeight, H + 50) });
  } finally {
    wrap.remove();
  }
  const img = canvas.toDataURL("image/jpeg", 0.95);
  if (receipt) {
    const w = 80;
    const h = (canvas.height * w) / canvas.width;
    const pdf = new jsPDF({ unit: "mm", format: [w, h] });
    pdf.addImage(img, "JPEG", 0, 0, w, h);
    pdf.save(filename);
    return;
  }
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const pw = 210;
  const ph = 297;
  const margin = 8;
  const usable = ph - margin * 2;
  const pxPerMm = canvas.width / pw;
  const ih = canvas.height / pxPerMm;
  if (ih <= usable * 1.04) {
    // Fits one page (or nearly): scale slightly instead of spilling a few lines onto page 2
    const k = Math.min(1, usable / ih);
    pdf.addImage(img, "JPEG", (pw - pw * k) / 2, margin, pw * k, ih * k);
    pdf.save(filename);
    return;
  }
  // Paginate: cut at the lowest safe break point that fits, draw each slice on its own page with page numbers
  const scale = canvas.width / W;
  const cuts = [0];
  while (cuts[cuts.length - 1] < canvas.height - 1) {
    const start = cuts[cuts.length - 1];
    const limit = start + usable * pxPerMm;
    if (limit >= canvas.height) { cuts.push(canvas.height); break; }
    let best = start;
    for (const [top, bottom] of breaks) for (const c of [top * scale, bottom * scale]) if (c > start + 40 * pxPerMm && c <= limit && c > best) best = c;
    cuts.push(best > start ? best : limit);
  }
  const pages = cuts.length - 1;
  for (let i = 0; i < pages; i++) {
    const y0 = cuts[i], y1 = Math.min(cuts[i + 1], canvas.height);
    const slice = document.createElement("canvas");
    slice.width = canvas.width; slice.height = Math.max(1, Math.round(y1 - y0));
    const sctx = slice.getContext("2d"); sctx.fillStyle = "#fff"; sctx.fillRect(0, 0, slice.width, slice.height);
    sctx.drawImage(canvas, 0, y0, canvas.width, slice.height, 0, 0, canvas.width, slice.height);
    if (i > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL("image/jpeg", 0.95), "JPEG", 0, margin, pw, slice.height / pxPerMm);
    pdf.setFontSize(8); pdf.setTextColor(120);
    pdf.text(`${i + 1} / ${pages}`, pw - margin, ph - 3, { align: "right" });
  }
  pdf.save(filename);
}
